import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PropTypes from 'prop-types';
import { useLiveQuery } from "dexie-react-hooks";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import {
    Building2, Users, Settings, BarChart3, ArrowLeft, Activity
} from "lucide-react";
import { db } from "../db";
import ClinicDashboardTab from "@/components/clinic/ClinicDashboardTab";
import ClinicStaffTab from "@/components/clinic/ClinicStaffTab";
import ClinicSettingsTab from "@/components/clinic/ClinicSettingsTab";
import ClinicAuditTab from "@/components/clinic/ClinicAuditTab";

const getRoleBadgeStyle = (role) => {
    switch (role) {
        case 'admin': return { bg: '#dbeafe', color: '#1e40af' };
        case 'doctor': return { bg: '#d1fae5', color: '#065f46' };
        case 'receptionist': return { bg: '#fef3c7', color: '#92400e' };
        default: return { bg: '#f1f5f9', color: '#475569' };
    }
};

export default function ClinicDetail({ token, onLogout }) {
    const { clinicId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const doctorId = localStorage.getItem('user_id');

    const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'dashboard');

    // Get clinic and membership from local DB
    const clinic = useLiveQuery(() => db.clinics.get(clinicId), [clinicId]);

    const myMembership = useLiveQuery(async () => {
        const members = await db.clinic_members
            .where('[clinic_id+user_id]')
            .equals([clinicId, doctorId])
            .first();
        return members;
    }, [clinicId, doctorId]);

    const myRole = myMembership?.role;
    const isAdmin = myRole === 'admin';

    const tabs = [
        { id: 'dashboard', label: 'Dashboard', icon: BarChart3, adminOnly: true },
        { id: 'staff', label: 'Staff', icon: Users },
        { id: 'audit', label: 'Audit Log', icon: Activity, adminOnly: true },
        { id: 'settings', label: 'Settings', icon: Settings, adminOnly: true }
    ].filter(tab => !tab.adminOnly || isAdmin);

    if (!clinic) {
        return (
            <div className="page-container">
                <Sidebar active="clinics" onLogout={onLogout} />
                <div className="main-content">
                    <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>Loading...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <Sidebar active="clinics" onLogout={onLogout} />
            <div className="main-content">
                {/* Header */}
                <div className="header" style={{ marginBottom: '1rem', maxWidth: '100%', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', minWidth: 0 }}>
                        <Button variant="ghost" onClick={() => navigate('/clinics')} style={{ padding: '0.5rem' }}>
                            <ArrowLeft size={20} />
                        </Button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{
                                width: '40px', height: '40px', borderRadius: '10px',
                                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <Building2 size={20} color="white" />
                            </div>
                            <div>
                                <h1 style={{ margin: 0 }}>{clinic.name}</h1>
                                <span style={{
                                    fontSize: '0.75rem', fontWeight: '600', textTransform: 'capitalize',
                                    padding: '0.125rem 0.5rem', borderRadius: '9999px',
                                    ...getRoleBadgeStyle(myRole)
                                }}>
                                    {myRole}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem', overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}>
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '0.5rem',
                                padding: '0.5rem 1rem', border: 'none', borderRadius: '8px',
                                background: activeTab === tab.id ? '#0ea5e9' : 'transparent',
                                color: activeTab === tab.id ? 'white' : '#64748b',
                                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                                flexShrink: 0, whiteSpace: 'nowrap'
                            }}
                        >
                            <tab.icon size={18} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content — keep mounted to preserve state across tab switches */}
                {isAdmin && (
                    <div style={{ display: activeTab === 'dashboard' ? undefined : 'none' }}>
                        <ClinicDashboardTab clinicId={clinicId} token={token} />
                    </div>
                )}
                <div style={{ display: activeTab === 'staff' ? undefined : 'none' }}>
                    <ClinicStaffTab clinicId={clinicId} token={token} isAdmin={isAdmin} doctorId={doctorId} />
                </div>
                {isAdmin && (
                    <div style={{ display: activeTab === 'audit' ? undefined : 'none' }}>
                        <ClinicAuditTab clinicId={clinicId} token={token} />
                    </div>
                )}
                {isAdmin && (
                    <div style={{ display: activeTab === 'settings' ? undefined : 'none' }}>
                        <ClinicSettingsTab clinicId={clinicId} token={token} clinic={clinic} />
                    </div>
                )}
            </div>
        </div>
    );
}

ClinicDetail.propTypes = {
    token: PropTypes.string,
    onLogout: PropTypes.func
};
